import { useLang } from "../i18n/LanguageContext";
import { Snowflake, Flame, ArrowRight, Phone, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1755914305030-bfc5c0196d76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxzdWJ1cmJhbiUyMGhvdXNlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc4MjEzODc5fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { t } = useLang();

  const handleQuote = (e) => {
    e.preventDefault();
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center pt-28 pb-20 overflow-hidden grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="DMV home" className="w-full h-full object-cover object-center scale-[1.04]" />
        {/* Dual gradient overlay: blue left, orange right, deep blue base */}
        <div className="absolute inset-0 bg-gradient-to-r from-cool/55 via-ink/85 to-heat/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/70 to-ink" />
      </div>

      {/* Floating temperature ribbons */}
      <div className="pointer-events-none absolute -left-24 top-1/3 hidden lg:flex items-center gap-3 rotate-[-8deg] opacity-90">
        <div className="h-px w-24 bg-cool" />
        <div className="h-12 w-12 rounded-2xl border border-cool/40 bg-cool/10 flex items-center justify-center backdrop-blur">
          <Snowflake className="text-cool" size={22} />
        </div>
      </div>
      <div className="pointer-events-none absolute -right-24 bottom-1/4 hidden lg:flex items-center gap-3 rotate-[6deg] opacity-90">
        <div className="h-12 w-12 rounded-2xl border border-heat/40 bg-heat/10 flex items-center justify-center backdrop-blur">
          <Flame className="text-heat" size={22} />
        </div>
        <div className="h-px w-24 bg-heat" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-white/15 bg-white/5 backdrop-blur text-xs sm:text-sm text-white/85" data-testid="hero-eyebrow">
            <span className="h-2 w-2 rounded-full bg-heat animate-pulse" />
            {t.hero.eyebrow}
          </div>

          <h1
            className="font-heading font-extrabold text-white tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl animate-rise"
            data-testid="hero-title"
          >
            {t.hero.title}{" "}
            <span className="text-shine">{t.hero.titleAccent}</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-white/80 max-w-2xl leading-relaxed animate-rise" style={{ animationDelay: "120ms" }} data-testid="hero-subtitle">
            {t.hero.subtitle}
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 animate-rise" style={{ animationDelay: "220ms" }}>
            <button
              onClick={handleQuote}
              data-testid="hero-cta-quote"
              className="btn-heat h-14 px-7 rounded-full font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.97] transition"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={18} />
            </button>
            <a
              href="tel:+12407618778"
              data-testid="hero-cta-call"
              className="h-14 px-7 rounded-full font-semibold inline-flex items-center justify-center gap-2 border border-white/20 bg-white/5 hover:bg-white/10 backdrop-blur transition"
            >
              <Phone size={18} />
              {t.hero.ctaSecondary}
            </a>
          </div>

          {/* Stats row */}
          <div className="mt-14 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl animate-rise" style={{ animationDelay: "320ms" }} data-testid="hero-stats">
            {t.hero.stats.map((s, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-ink-card/60 backdrop-blur p-4">
                <div className={`font-heading font-extrabold text-2xl sm:text-3xl ${i === 1 ? "text-heat" : i === 0 ? "text-cool" : "text-white"}`}>
                  {s.value}
                </div>
                <div className="text-xs text-white/65 mt-1 leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom temperature divider */}
      <div className="absolute bottom-0 inset-x-0 divider-temp" />
    </section>
  );
};
